import React, { useState, useEffect } from 'react';
import { Card } from '../common';

/**
 * MCQ Question Component with Image Grid
 * Auto-advances after selection (no click needed)
 */
const MCQQuestion = ({ 
  question, 
  onAnswer, 
  autoAdvanceDelay = 800 
}) => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    // Reset when question changes
    setSelectedOption(null);
    setIsAnimating(false);
  }, [question.id]);

  const handleOptionSelect = (option) => {
    if (selectedOption) return; // Prevent multiple selections
    
    setSelectedOption(option.id);
    setIsAnimating(true);

    // Auto-advance after delay
    setTimeout(() => {
      onAnswer({
        questionId: question.id,
        answerId: option.id,
        answer: option.text,
        score: option.score,
        trait: option.trait,
        category: question.category
      });
    }, autoAdvanceDelay);
  };

  return (
    <div className="animate-fade-in">
      <Card className="max-w-4xl mx-auto">
        {/* Question Header */}
        <div className="text-center mb-8">
          <div className="text-6xl mb-4 animate-bounce">
            {question.image}
          </div>
          <h2 className="text-2xl font-bold text-slate-100 mb-2">
            {question.question}
          </h2>
          <p className="text-slate-400 text-sm">
            Select your answer
          </p>
        </div>

        {/* Options Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {question.options.map((option) => {
            const isSelected = selectedOption === option.id;
            const isOtherSelected = selectedOption && selectedOption !== option.id;

            return (
              <button
                key={option.id}
                onClick={() => handleOptionSelect(option)}
                disabled={selectedOption !== null}
                className={`
                  relative p-6 rounded-xl border-2 transition-all duration-300 transform
                  ${isSelected 
                    ? 'border-primary-500 bg-primary-500/20 scale-105 shadow-lg shadow-primary-500/50' 
                    : isOtherSelected
                    ? 'border-slate-700 bg-slate-800/50 opacity-50 scale-95'
                    : 'border-slate-700 bg-slate-800 hover:border-primary-500/50 hover:bg-slate-700 hover:scale-105'
                  }
                  ${selectedOption ? 'cursor-not-allowed' : 'cursor-pointer'}
                `}
              >
                {/* Option Image/Emoji */}
                <div className={`text-5xl mb-4 transition-transform duration-300 ${isSelected ? 'scale-125' : ''}`}>
                  {option.image}
                </div>

                {/* Option Text */}
                <p className={`text-base font-medium transition-colors ${
                  isSelected ? 'text-primary-100' : 'text-slate-300'
                }`}>
                  {option.text}
                </p>

                {/* Selection Indicator */}
                {isSelected && (
                  <div className="absolute top-3 right-3 animate-fade-in">
                    <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Loading Indicator */}
        {isAnimating && (
          <div className="mt-6 text-center">
            <div className="inline-flex items-center gap-2 text-primary-400">
              <div className="w-2 h-2 bg-primary-400 rounded-full animate-pulse"></div>
              <span className="text-sm font-medium">Moving to next question...</span>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default MCQQuestion;
